import { Flex, IconCheck, Skeleton } from '@vtex/admin-ui'
import type { DefaultProps, Item } from 'typings'
import styles from './listSelector.css'

export type ListSelectorProps = {
  values: string[]
  options?: Item[]
  loading?: boolean
  disabled?: boolean
  size?: number
  onChange: (selectedIds: string[]) => void
}

interface LoadingProps extends DefaultProps {
  loading: boolean
  size?: number
}

const DEFAULT_SIZE = 10

function LoadingWrapper(props: LoadingProps) {
  const { children, loading, size } = props

  return (
    <>
      {loading
        ? Array(size ?? DEFAULT_SIZE)
            .fill(0)
            .map((_, index) => (
              <Skeleton
                key={index}
                data-testid="list-selector-skeleton"
                className={styles.skeleton}
              />
            ))
        : children}
    </>
  )
}

export function ListSelector(props: ListSelectorProps) {
  const { disabled, loading, onChange, options, size, values } = props

  const handleSelection = (id: string, selected: boolean) => {
    if (disabled) return

    const newIds = selected
      ? [...values.filter((x) => x !== id)]
      : [...values, id]

    onChange(newIds)
  }

  return (
    <Flex className={styles.list} direction="column">
      <LoadingWrapper loading={!!loading} size={size}>
        {options?.map(({ id, name }, index) => {
          const checked = values.includes(id)

          return (
            <div
              className={styles.option}
              key={index}
              onClick={() => handleSelection(id, checked)}
              role="checkbox"
              tabIndex={disabled ? -1 : 0}
              aria-checked={checked}
              aria-readonly={disabled}
              aria-label={name}
              onKeyDown={(event) => {
                if (['Enter', 'Space'].includes(event.code)) {
                  handleSelection(id, checked)
                  event.preventDefault()
                }
              }}
            >
              <span title={name}> {name} </span>

              {checked && <IconCheck data-testid="check-icon" />}
            </div>
          )
        })}
      </LoadingWrapper>
    </Flex>
  )
}
